import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, MerchandiseItem, SellerPaymentSettings } from '../../types';
import {
  getSellerPaymentSettingsDB,
  saveSellerPaymentSettingsDB,
  DEFAULT_SELLER_PAYMENT_SETTINGS,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS
} from '../../services/db';
import { uploadToCloudinary } from '../../lib/cloudinary';
import { useToast } from '../common/Toast';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import {
  QrCode,
  Building2,
  CreditCard,
  Upload,
  Check,
  Copy,
  ShieldCheck,
  Info,
  RefreshCw,
  Sparkles,
  ExternalLink,
  Save,
  UserCheck,
  HelpCircle,
  Eye,
  AlertCircle,
  Loader2
} from 'lucide-react';

const MALAYSIAN_BANKS = [
  'Bank Islam Malaysia Berhad',
  'Maybank (Malayan Banking Berhad)',
  'CIMB Bank Berhad',
  'Public Bank Berhad',
  'RHB Bank Berhad',
  'Hong Leong Bank Berhad',
  'AmBank (M) Berhad',
  'Bank Muamalat Malaysia Berhad',
  'Agrobank (Bank Pertanian)',
  'Affin Bank Berhad',
  'Alliance Bank Malaysia Berhad',
  'Bank Kerjasama Rakyat (Bank Rakyat)',
  'Bank Simpanan Nasional (BSN)',
  'Touch \'n Go Digital (TNG eWallet)',
  'Lain-lain (Bank Tempatan)'
];

interface SellerSettingsViewProps {
  currentUser: UserProfile;
  members: UserProfile[];
  items: MerchandiseItem[];
}

export const SellerSettingsView: React.FC<SellerSettingsViewProps> = ({
  currentUser,
  members,
  items
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isExecutive = Boolean(
    currentUser &&
    (isSuperAdmin(currentUser) ||
      hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari']))
  );

  // Available sellers selection for admins / executives
  const availableSellers = React.useMemo(() => {
    const list: { id: string; name: string; isPusat?: boolean }[] = [
      { id: 'ASMM-001', name: 'PSSASMM Pusat (Pentadbiran Utama)', isPusat: true }
    ];

    // Collect sellers from existing merchandise items
    items.forEach(item => {
      if (item.userId && !list.some(s => s.id.toUpperCase() === item.userId?.toUpperCase())) {
        const member = members.find(m => m.id.toUpperCase() === item.userId?.toUpperCase());
        list.push({
          id: item.userId.toUpperCase(),
          name: member ? `${member.name} (${item.userId})` : `Penjual ${item.userId}`
        });
      }
    });

    // Add current user if not in list
    if (currentUser?.id && !list.some(s => s.id.toUpperCase() === currentUser.id.toUpperCase())) {
      list.push({
        id: currentUser.id.toUpperCase(),
        name: `${currentUser.name} (${currentUser.id})`
      });
    }

    return list;
  }, [items, members, currentUser]);

  const defaultSellerId = React.useMemo(() => {
    if (isExecutive) return 'ASMM-001';
    return (currentUser?.id || 'ASMM-001').toUpperCase();
  }, [isExecutive, currentUser]);

  const [activeSellerId, setActiveSellerId] = useState<string>(defaultSellerId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploadingQr, setIsUploadingQr] = useState<boolean>(false);
  const [isCopiedDemo, setIsCopiedDemo] = useState<boolean>(false);

  // Form Fields
  const [bankName, setBankName] = useState<string>(DEFAULT_SELLER_PAYMENT_SETTINGS.bankName);
  const [accountNo, setAccountNo] = useState<string>(DEFAULT_SELLER_PAYMENT_SETTINGS.accountNo);
  const [accountHolder, setAccountHolder] = useState<string>(DEFAULT_SELLER_PAYMENT_SETTINGS.accountHolder);
  const [sellerName, setSellerName] = useState<string>(DEFAULT_SELLER_PAYMENT_SETTINGS.sellerName || '');
  const [qrImageUrl, setQrImageUrl] = useState<string>(DEFAULT_SELLER_PAYMENT_SETTINGS.qrImageUrl || '');
  const [paymentInstructions, setPaymentInstructions] = useState<string>(
    DEFAULT_SELLER_PAYMENT_SETTINGS.paymentInstructions || ''
  );
  const [qrEnabled, setQrEnabled] = useState<boolean>(true);

  // Digital 1:1 SVG states for preview
  const [digitalSvgString, setDigitalSvgString] = useState<string>('');
  const [isProcessingSvg, setIsProcessingSvg] = useState<boolean>(false);

  useEffect(() => {
    if (!qrImageUrl) {
      setDigitalSvgString('');
      return;
    }

    const isAlreadySvg = qrImageUrl.startsWith('data:image/svg+xml') || qrImageUrl.trim().startsWith('<svg');
    if (isAlreadySvg) {
      if (qrImageUrl.trim().startsWith('<svg')) {
        setDigitalSvgString(qrImageUrl);
      } else if (qrImageUrl.startsWith('data:image/svg+xml')) {
        try {
          const decoded = decodeURIComponent(
            qrImageUrl.replace('data:image/svg+xml;charset=utf-8,', '').replace('data:image/svg+xml,', '')
          );
          setDigitalSvgString(decoded);
        } catch (e) {}
      }
      return;
    }

    let isMounted = true;
    setIsProcessingSvg(true);

    processUploadedQrToSvg(qrImageUrl, { colorDark: '#ED0080' })
      .then((res) => {
        if (!isMounted || !res) return;
        setDigitalSvgString(res.svgString);
      })
      .catch((err) => {
        console.warn('Auto SVG regeneration preview failed:', err);
      })
      .finally(() => {
        if (isMounted) setIsProcessingSvg(false);
      });

    return () => {
      isMounted = false;
    };
  }, [qrImageUrl]);

  // Load seller settings whenever activeSellerId changes
  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);

    getSellerPaymentSettingsDB(activeSellerId)
      .then((settings) => {
        if (isCancelled) return;
        setBankName(settings.bankName || DEFAULT_SELLER_PAYMENT_SETTINGS.bankName);
        setAccountNo(settings.accountNo || DEFAULT_SELLER_PAYMENT_SETTINGS.accountNo);
        setAccountHolder(settings.accountHolder || DEFAULT_SELLER_PAYMENT_SETTINGS.accountHolder);
        setSellerName(settings.sellerName || (activeSellerId === 'ASMM-001' ? 'PSSASMM Pusat' : `Penjual ${activeSellerId}`));
        setQrImageUrl(settings.qrImageUrl || DEFAULT_SELLER_PAYMENT_SETTINGS.qrImageUrl || '');
        setPaymentInstructions(
          settings.paymentInstructions || DEFAULT_SELLER_PAYMENT_SETTINGS.paymentInstructions || ''
        );
        setQrEnabled(settings.qrEnabled !== false);
      })
      .catch((err) => {
        console.warn('Error loading seller settings:', err);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [activeSellerId]);

  // Handle Cloudinary QR Upload
  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Format Tidak Sah', 'Sila muat naik imej PNG, JPG, atau WebP untuk kod QR.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('error', 'Saiz Imej Terlalu Besar', 'Maksimum saiz imej kod QR ialah 5MB.');
      return;
    }

    setIsUploadingQr(true);
    showToast('info', 'Memuat Naik Kod QR...', 'Menyimpan fail ke Cloudinary CDN...');

    try {
      const res = await uploadToCloudinary(file, {
        folder: 'asmm/seller-qr',
        publicId: `qr-seller-${activeSellerId.toLowerCase()}-${Date.now().toString().slice(-4)}`
      });

      setQrImageUrl(res.secure_url);
      showToast('success', 'Kod QR Dimuat Naik!', 'Imej DuitNow QR berjaya disimpan ke CDN.');
    } catch (err: any) {
      console.error('Cloudinary QR upload failed:', err);
      showToast('error', 'Gagal Memuat Naik', err?.message || 'Sila cuba lagi.');
    } finally {
      setIsUploadingQr(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Reset to default Persatuan QR
  const handleResetToPersatuan = () => {
    setQrImageUrl(DEFAULT_PERSATUAN_PAYMENT_SETTINGS.qrImageUrl || '');
    setBankName(DEFAULT_PERSATUAN_PAYMENT_SETTINGS.bankName);
    setAccountNo(DEFAULT_PERSATUAN_PAYMENT_SETTINGS.accountNo);
    setAccountHolder(DEFAULT_PERSATUAN_PAYMENT_SETTINGS.accountHolder);
    showToast('info', 'Ditetapkan Semula', 'Maklumat akaun telah diselaraskan dengan akaun rasmi persatuan.');
  };

  // Save Settings
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountNo.trim()) {
      showToast('error', 'Diperlukan', 'Sila masukkan nombor akaun bank yang sah.');
      return;
    }

    if (!accountHolder.trim()) {
      showToast('error', 'Diperlukan', 'Sila masukkan nama pemegang akaun bank.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: SellerPaymentSettings = {
        sellerId: activeSellerId,
        sellerName: sellerName.trim() || undefined,
        bankName: bankName.trim(),
        accountNo: accountNo.trim().replace(/\s+/g, ''),
        accountHolder: accountHolder.trim().toUpperCase(),
        qrImageUrl: qrImageUrl.trim(),
        paymentInstructions: paymentInstructions.trim(),
        qrEnabled,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.name
      };

      await saveSellerPaymentSettingsDB(payload, currentUser.name);

      showToast(
        'success',
        'Tetapan Bayaran Disimpan!',
        `Saluran bayaran bagi [${activeSellerId}] telah dikemaskini. Pembeli barangan anda akan melihat maklumat akaun ini secara automatik.`
      );
    } catch (err: any) {
      console.error('Failed to save seller payment settings:', err);
      showToast('error', 'Ralat', 'Gagal menyimpan tetapan bayaran. Sila cuba lagi.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyDemoAcc = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(accountNo.trim());
      setIsCopiedDemo(true);
      setTimeout(() => setIsCopiedDemo(false), 2000);
      showToast('success', 'Disalin!', `${accountNo} telah disalin.`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner Card */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-asmm-red rounded-full text-xs font-extrabold uppercase tracking-wider border border-red-100">
              <QrCode className="w-3.5 h-3.5" />
              <span>Saluran Bayaran & Kod QR Penjual</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              Tetapan Kod QR & Akaun Bank Penjual
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 max-w-2xl font-medium leading-relaxed">
              Konfigurasikan akaun bank dan kod QR DuitNow peribadi atau persatuan anda.
              Apabila ahli atau orang awam membeli produk yang anda senaraikan, sistem akan memaparkan kod QR dan rujukan bank anda secara dinamik.
            </p>
          </div>

          {/* Admin Switch Seller Dropdown */}
          {isExecutive && availableSellers.length > 1 && (
            <div className="bg-slate-50 border border-slate-200 p-3 rounded-2xl shrink-0 space-y-1.5">
              <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
                Pilih Profil Penjual
              </label>
              <select
                value={activeSellerId}
                onChange={(e) => setActiveSellerId(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:border-asmm-red focus:outline-none cursor-pointer"
              >
                {availableSellers.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Main Grid: Form (Left) & Realtime Buyer Preview (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Form Column */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-xs">
          <form onSubmit={handleSave} className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-asmm-red" />
                <h4 className="text-sm font-black text-slate-900">
                  Butiran Akaun Bank & Pindahan
                </h4>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-[11px] font-bold text-slate-600 cursor-pointer flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={qrEnabled}
                    onChange={(e) => setQrEnabled(e.target.checked)}
                    className="rounded text-asmm-red focus:ring-asmm-red"
                  />
                  <span>DuitNow QR Aktif</span>
                </label>
              </div>
            </div>

            {/* Nama Paparan Penjual */}
            <div className="space-y-1.5">
              <label className="block text-xs font-extrabold text-slate-800">
                Nama Paparan Penjual / Entiti Kedai
              </label>
              <input
                type="text"
                value={sellerName}
                onChange={(e) => setSellerName(e.target.value)}
                placeholder="Contoh: PSSASMM Pusat / Kedai Silat Gelanggang Lunas"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900"
              />
              <p className="text-[10px] text-slate-400 font-medium">
                Nama ini dipaparkan kepada pembeli untuk mengesahkan bahawa mereka membuat bayaran kepada penjual yang sah.
              </p>
            </div>

            {/* Bank Name Selector */}
            <div className="space-y-1.5">
              <label className="block text-xs font-extrabold text-slate-800">
                Nama Bank Penerima
              </label>
              <select
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900 cursor-pointer"
              >
                {MALAYSIAN_BANKS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            {/* Account Number & Account Holder */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-extrabold text-slate-800">
                  Nombor Akaun Bank
                </label>
                <input
                  type="text"
                  value={accountNo}
                  onChange={(e) => setAccountNo(e.target.value)}
                  placeholder="Contoh: 02011010087654"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-mono font-bold text-slate-900"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-extrabold text-slate-800">
                  Nama Pemegang Akaun
                </label>
                <input
                  type="text"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder="Contoh: PERSATUAN SENI SILAT AWANG SULUNG..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900"
                  required
                />
              </div>
            </div>

            {/* QR Code Upload / URL Section */}
            <div className="space-y-3 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-extrabold text-slate-800">
                  Imej Kod QR DuitNow Penjual
                </label>
                <button
                  type="button"
                  onClick={handleResetToPersatuan}
                  className="text-[10px] font-extrabold text-slate-500 hover:text-slate-800 flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Guna QR Persatuan</span>
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                {/* QR Thumbnail */}
                <div className="w-28 h-28 shrink-0 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex items-center justify-center p-2 relative overflow-hidden group">
                  {qrImageUrl ? (
                    <img
                      src={qrImageUrl}
                      alt="DuitNow QR Preview"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center space-y-1">
                      <QrCode className="w-8 h-8 text-slate-300 mx-auto" />
                      <span className="text-[9px] font-bold text-slate-400 block">Tiada Kod QR</span>
                    </div>
                  )}

                  {isUploadingQr && (
                    <div className="absolute inset-0 bg-white/90 backdrop-blur-xs flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-asmm-red border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {/* Upload Action and URL input */}
                <div className="flex-1 w-full space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleQrUpload}
                      className="hidden"
                      id="seller-qr-file-input"
                    />
                    <label
                      htmlFor="seller-qr-file-input"
                      className={`px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-extrabold transition-all shadow-xs flex items-center gap-2 cursor-pointer ${isUploadingQr ? 'opacity-60 pointer-events-none' : ''
                        }`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      <span>{isUploadingQr ? 'Memuat Naik...' : 'Muat Naik Imej Kod QR'}</span>
                    </label>

                    {qrImageUrl && (
                      <button
                        type="button"
                        onClick={() => setQrImageUrl('')}
                        className="px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-xl transition-all cursor-pointer"
                      >
                        Padam
                      </button>
                    )}
                  </div>

                  <input
                    type="url"
                    value={qrImageUrl}
                    onChange={(e) => setQrImageUrl(e.target.value)}
                    placeholder="Atau tampal URL imej Cloudinary / HTTPS..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-[11px] font-mono text-slate-800"
                  />
                  <p className="text-[10px] text-slate-400 font-medium">
                    Format imej disyorkan: PNG / JPG resolusi tinggi daripada aplikasi perbankan rasmi (MAE / CIMB / Bank Islam / DuitNow).
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="space-y-1.5 pt-3 border-t border-slate-100">
              <label className="block text-xs font-extrabold text-slate-800">
                Arahan Pembayaran untuk Pembeli
              </label>
              <textarea
                rows={3}
                value={paymentInstructions}
                onChange={(e) => setPaymentInstructions(e.target.value)}
                placeholder="Contoh: Sila masukkan No. Pesanan atau No Telefon anda dalam ruangan rujukan bank..."
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-medium text-slate-900 resize-none leading-relaxed"
              />
              <p className="text-[10px] text-slate-400 font-medium">
                Arahan ini akan dipaparkan dalam kotak panduan semasa pembeli mengimbas QR kod anda.
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving || isUploadingQr}
                className="px-7 py-3 bg-asmm-red hover:bg-red-700 text-white rounded-full text-xs font-extrabold transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Menyimpan...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Simpan Tetapan Bayaran</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Live Buyer Preview Column */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-800">
                  Pratonton Paparan Pembeli
                </h4>
              </div>
              <span className="text-[10px] font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                Live Preview
              </span>
            </div>

            <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
              Ini adalah paparan sebenar yang akan dilihat oleh pelanggan semasa membuat pembayaran bagi produk anda.
            </p>

            {/* Realistic Modal Preview Container */}
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-4 shadow-sm space-y-4">
              {/* Demo Top Banner */}
              <div className="bg-gradient-to-br from-red-50 to-amber-50/40 p-3.5 rounded-xl border border-red-100 flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <span className="text-[9px] font-black uppercase tracking-wider text-asmm-red bg-white px-2 py-0.5 rounded-full border border-red-200">
                    Imbas & Bayar QR Rasmi
                  </span>
                  <div className="font-extrabold text-slate-900 text-xs">
                    Baju Silat Merah Muda <span className="text-slate-500 font-medium">(x1)</span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Rujukan Tempahan: <span className="font-mono font-bold text-slate-700">#DEMO99</span>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <span className="text-[9px] font-bold text-slate-500 uppercase block">Jumlah</span>
                  <span className="text-base font-black text-asmm-red font-mono">
                    RM 100.00
                  </span>
                </div>
              </div>

              {/* QR Image Box */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-2.5">
                <div className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-[#ED0080]/10 text-[#ED0080] rounded-full text-[10px] font-black tracking-wide uppercase mx-auto">
                  <QrCode className="w-3 h-3" />
                  <span>DuitNow QR</span>
                </div>

                <div className="relative mx-auto w-40 h-40 bg-white p-2.5 rounded-xl border-2 border-slate-200 shadow-xs flex items-center justify-center">
                  {isProcessingSvg ? (
                    <div className="flex flex-col items-center justify-center text-center p-2 space-y-1">
                      <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                      <span className="text-[10px] text-slate-500 font-bold">Menjana Digital SVG...</span>
                    </div>
                  ) : digitalSvgString ? (
                    <div
                      className="w-full h-full aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:aspect-square [&>svg]:block"
                      dangerouslySetInnerHTML={{ __html: digitalSvgString }}
                    />
                  ) : qrImageUrl ? (
                    <img
                      src={qrImageUrl}
                      alt="DuitNow QR"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center space-y-1 p-2">
                      <QrCode className="w-10 h-10 text-slate-300 mx-auto" />
                      <span className="text-[10px] text-slate-400 font-bold block">
                        Sila muat naik QR anda
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-[10px] text-slate-600 font-medium leading-relaxed max-w-xs mx-auto">
                  Buka aplikasi perbankan (<strong>MAE, CIMB, Bank Islam, TnG eWallet</strong>) dan imbas kod QR di atas.
                </p>
              </div>

              {/* Bank Account Details Card */}
              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold flex items-center gap-1">
                    <Building2 className="w-3 h-3 text-slate-400" />
                    <span>Bank:</span>
                  </span>
                  <span className="font-extrabold text-slate-900 truncate max-w-[180px]">
                    {bankName}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <span className="text-slate-500 font-bold">No. Akaun:</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono font-extrabold text-slate-900 text-xs">
                      {accountNo || '—'}
                    </span>
                    {accountNo && (
                      <button
                        type="button"
                        onClick={copyDemoAcc}
                        className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[9px] font-extrabold transition-all flex items-center gap-1 cursor-pointer"
                        title="Salin Nombor Akaun"
                      >
                        {isCopiedDemo ? <Check className="w-2.5 h-2.5 text-emerald-600" /> : <Copy className="w-2.5 h-2.5" />}
                        <span>{isCopiedDemo ? 'Disalin' : 'Salin'}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <span className="text-slate-500 font-bold">Nama Akaun:</span>
                  <span className="font-bold text-slate-700 text-right text-[10px] truncate max-w-[180px]">
                    {accountHolder || '—'}
                  </span>
                </div>

                {paymentInstructions && (
                  <div className="pt-1 border-t border-slate-100 text-[10px] text-amber-800 bg-amber-50/70 p-2 rounded-lg border border-amber-200/50 leading-relaxed font-medium">
                    <div className="font-bold flex items-center gap-1 mb-0.5">
                      <Info className="w-3 h-3 text-amber-600" />
                      <span>Nota Bayaran:</span>
                    </div>
                    {paymentInstructions}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
